/**
 * Who may do what in a design studio: its roles and their permissions, for
 * Better Auth (convex/auth.ts, src/lib/auth-client.ts) and for Ours' own
 * checks (convex/model/access.ts). Shared by the server and the web.
 *
 * Owner and Admin are fixed and may do everything. Every studio also gets
 * Designer and Assistant, roles of its own (Better Auth's dynamic access
 * control: organizationRole rows) that it can change, next to any it adds.
 * A brand's people are "member": the brand console asks nothing more.
 *
 * Clients are not here: they are let into one workspace at a time with their
 * own four permissions (src/lib/permissions.ts).
 */
import { createAccessControl } from "better-auth/plugins/access";
import { defaultStatements } from "better-auth/plugins/organization/access";

export const studioStatements = {
  ...defaultStatements,
  finds: ["add"],
  rooms: ["change"],
  builds: ["run"],
  clients: ["invite"],
  workspaces: ["manage"],
  billing: ["view"],
  console: ["open"],
} as const;

export const studioAc = createAccessControl(studioStatements);

type Statement = typeof studioStatements;
export type StudioPermission = { [K in keyof Statement]?: Statement[K][number][] };

/**
 * The permissions the console shows, in its order: the first five count in
 * the workspaces a person works on, the rest across the studio. Seeing the
 * rooms, finds and 3D comes with being in the studio at all.
 */
export const STUDIO_KEYS = [
  "finds",
  "rooms",
  "builds",
  "clients",
  "workspaces",
  "people",
  "billing",
  "console",
] as const;
export type StudioKey = (typeof STUDIO_KEYS)[number];
export const WORKSPACE_KEYS: readonly StudioKey[] = ["finds", "rooms", "builds", "clients"];

/** What each key grants in Better Auth's terms. */
const GRANTS: Record<StudioKey, StudioPermission> = {
  finds: { finds: ["add"] },
  rooms: { rooms: ["change"] },
  builds: { builds: ["run"] },
  clients: { clients: ["invite"] },
  workspaces: { workspaces: ["manage"] },
  // Inviting and removing people, giving them roles, and the roles themselves.
  people: {
    member: ["create", "update", "delete"],
    invitation: ["create", "cancel"],
    ac: ["create", "read", "update", "delete"],
  },
  billing: { billing: ["view"] },
  console: { console: ["open"] },
};

/** Better Auth's permission for a set of keys. Every role may read the roles. */
export function permissionFor(keys: readonly StudioKey[]): StudioPermission {
  const out: Record<string, Set<string>> = { ac: new Set(["read"]) };
  for (const key of keys) {
    for (const [resource, actions] of Object.entries(GRANTS[key])) {
      out[resource] ??= new Set();
      for (const action of actions as string[]) out[resource].add(action);
    }
  }
  return Object.fromEntries(Object.entries(out).map(([k, v]) => [k, [...v]])) as StudioPermission;
}

/** The keys a permission grants in full. */
export function keysOf(permission: Record<string, readonly string[]> | null | undefined): StudioKey[] {
  if (!permission) return [];
  return STUDIO_KEYS.filter((key) =>
    Object.entries(GRANTS[key]).every(([resource, actions]) =>
      (actions as string[]).every((action) => permission[resource]?.includes(action)),
    ),
  );
}

/** Roles that are the same in every studio, and may do everything there. */
export const FIXED_ROLES = ["owner", "admin"] as const;
export const isFixedRole = (role: string) => (FIXED_ROLES as readonly string[]).includes(role);

/** The roles each new studio starts with, which it can change. */
export const STARTING_ROLES: { role: string; keys: StudioKey[] }[] = [
  { role: "designer", keys: ["finds", "rooms", "builds", "clients"] },
  { role: "assistant", keys: ["finds"] },
];

/** The role someone is invited with unless the inviter picks another. */
export const DEFAULT_INVITE_ROLE = "designer";

/** Most roles a studio can have of its own (Designer and Assistant included). */
export const MAX_STUDIO_ROLES = 12;

const everything = permissionFor(STUDIO_KEYS);

export const studioRoles = {
  owner: studioAc.newRole({ ...everything, organization: ["update", "delete"] }),
  admin: studioAc.newRole({ ...everything, organization: ["update"] }),
  // A brand's people: the console is theirs whatever the role.
  member: studioAc.newRole({ ac: ["read"] }),
};

/** A role's name as people read it: "junior-designer" → "Junior designer". */
export function roleLabel(role: string) {
  const words = role.replace(/[-_]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The name Better Auth keeps for a role a studio types. */
export function roleKey(label: string) {
  return label
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}
